import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions } from '@material-ui/core';
import { ClinicFormProps } from '../../../CreateClinic/types';
import { ClinicCondition, ExistingClinicWithTreatmentsAndPromo } from '../../../types';
import styled from 'styled-components';
import { useClinics } from '../../../ClinicsList/hooks/useClinics';
import ReactSelect from 'react-select';
import { useQuery, useMutation } from '@tanstack/react-query';
import { getClinicConditions, addConditionFromAnotherClinic } from '../../../../../apis/clinics/requests';
import { useCondition } from '../../../../ConditionSelector/useCondition';
import { useConditionTitle } from '../tabs/ClinicConditionsTab/useConditionTitle';
import { HealtheraButton, SecondaryButton } from '../../../../../healtheraComponents';

interface ConditionOptionProps {
  condition: ClinicCondition;
}

const ConditionOption = ({ condition, isExisting }: ConditionOptionProps & { isExisting?: boolean }) => {
  const { condition: conditionDetails } = useCondition(condition.condition_id);
  const title = useConditionTitle(conditionDetails);

  return (
    <div style={{ opacity: 1 }}>
      {title || condition.condition_id}
      {isExisting && ' (already added)'}
    </div>
  );
};

interface SelectClinicConditionDialogProps extends ClinicFormProps {
  open: boolean;
  onClose: () => void;
  currentClinicId: string;
  onSelect: (condition: ClinicCondition) => void;
}

const SelectClinicConditionDialog = ({ open, onClose, currentClinicId, onSelect, form }: SelectClinicConditionDialogProps) => {
  const [selectedClinicId, setSelectedClinicId] = useState<string | null>(null);
  const [selectedCondition, setSelectedCondition] = useState<any>(null);
  const { clinics } = useClinics(0, 50);
  const { data: conditionsData, isLoading } = useQuery({
    queryKey: ['clinicConditions', selectedClinicId],
    queryFn: () => selectedClinicId ? getClinicConditions(selectedClinicId) : null,
    enabled: Boolean(selectedClinicId)
  });

  const currentClinicConditions = form.watch('clinic_conditions');
  const existingConditionIds = currentClinicConditions.map(condition => condition.condition_id);

  const otherClinics = (clinics as ExistingClinicWithTreatmentsAndPromo[]).filter(clinic => clinic._id !== currentClinicId);
  const clinicOptions = otherClinics.map(clinic => ({
    value: clinic._id,
    label: clinic.name
  }));

  const conditionOptions = (conditionsData?.data?.clinic_conditions || [])
    .map((condition: ClinicCondition) => {
      const isExisting = existingConditionIds.includes(condition.condition_id);
      return {
        value: condition._id,
        label: <ConditionOption condition={condition} isExisting={isExisting} />,
        condition,
        isDisabled: isExisting
      };
    });

  const createConditionMutation = useMutation({
    mutationFn: (condition: ClinicCondition) => addConditionFromAnotherClinic({
      condition_id: condition._id,
      clinic_id: currentClinicId
    }),
    onSuccess: () => {
      handleClose();
    }
  });

  const handleClinicChange = (option: any) => {
    setSelectedClinicId(option?.value || null);
    setSelectedCondition(null);
  };

  const handleConditionSelect = (option: any) => {
    setSelectedCondition(option);
  };

  const handleAddCondition = () => {
    if (selectedCondition?.condition && selectedClinicId) {
      const originalCondition = selectedCondition.condition;
      onSelect(originalCondition);
      createConditionMutation.mutate(originalCondition);
    }
  };

  const handleClose = () => {
    setSelectedClinicId(null);
    setSelectedCondition(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select Condition from Another Clinic</DialogTitle>
      <StyledDialogContent>
        <div>
          <label>Select Clinic</label>
          <ReactSelect
            options={clinicOptions}
            onChange={handleClinicChange}
            value={clinicOptions.find(option => option.value === selectedClinicId)}
            placeholder="Select a clinic..."
            isClearable
            classNamePrefix="select"
            className="select-container"
          />
        </div>
        {selectedClinicId && (
          <div>
            <label>Select Condition</label>
            {isLoading ? (
              <div>Loading conditions...</div>
            ) : (
              <ReactSelect
                options={conditionOptions}
                onChange={handleConditionSelect}
                value={selectedCondition}
                placeholder={conditionOptions.length === 0 ? 'No conditions found' : 'Select a condition...'}
                isClearable
                classNamePrefix="select"
                className="select-container"
                styles={{
                  menuList: (base) => ({
                    ...base,
                  }),
                }}
              />
            )}
          </div>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <SecondaryButton onClick={handleClose}>
          Cancel
        </SecondaryButton>
        <HealtheraButton
          onClick={handleAddCondition}
          disabled={!selectedCondition}
        >
          Add Condition
        </HealtheraButton>
      </StyledDialogActions>
    </Dialog>
  );
};

export default SelectClinicConditionDialog;

const StyledDialogContent = styled(DialogContent)`
  min-height: 390px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .select-container {
    .select__menu {
      max-height: 300px;
      overflow-y: auto;
    }
  }
`;

const StyledDialogActions = styled(DialogActions)`
  padding: 16px 24px;
  display: flex;
  justify-content: center;
  gap: 16px;
`;
